import { parse as parsePartialJSON } from 'partial-json'

export interface SSEStreamOptions<T> {
  response: Response
  onPartialResult: (accumulated: string, parsed: T) => void
  onComplete: (data: { record_id?: string; [key: string]: any }) => void
  onError: (error: Error) => void
  isValidPartial: (parsed: any) => parsed is T
}

export async function readSSEStream<T>(options: SSEStreamOptions<T>): Promise<void> {
  const { response, onPartialResult, onComplete, onError, isValidPartial } = options

  const reader = response.body?.getReader()
  const decoder = new TextDecoder()
  let accumulated = ''
  let buffer = ''

  if (!reader) throw new Error('No response body')

  const processLine = (line: string) => {
    if (!line.startsWith('data: ')) return
    const data = line.slice(6).trim()
    if (!data) return

    try {
      const parsed = JSON.parse(data)

      if (parsed.type === 'complete') {
        onComplete(parsed)
        return 'complete'
      }

      if (parsed.type === 'error') {
        onError(new Error(parsed.error))
        return 'error'
      }

      if (parsed.accumulated) {
        accumulated = parsed.accumulated
        try {
          const partialResult = parsePartialJSON(accumulated)
          if (isValidPartial(partialResult)) {
            onPartialResult(accumulated, partialResult)
          }
        } catch {
          // partial JSON not ready yet
        }
      }
    } catch (e) {
      console.error('Failed to parse SSE data:', e)
    }
    return undefined
  }

  try {
    while (true) {
      const { done, value } = await reader.read()
      if (done) break

      buffer += decoder.decode(value, { stream: true })
      const lines = buffer.split('\n')
      buffer = lines.pop() || ''

      for (const line of lines) {
        const result = processLine(line)
        if (result === 'complete' || result === 'error') return
      }
    }

    // Process remaining buffer
    if (buffer.trim()) {
      processLine(buffer.trim())
    }
  } finally {
    reader.releaseLock()
  }
}
